// @ts-nocheck
// Minha Assinatura — tela onde user vê detalhes do plano atual,
// próxima cobrança, e tem ações de "Trocar plano" e "Cancelar assinatura".
//
// Acessada via Profile → "Plano e cobrança".
//
// Em users free, mostra CTA pra fazer upgrade. Em users pagantes, mostra os
// dados da assinatura viva — hoje sempre da loja (App Store / Google Play),
// e do Mercado Pago quando o checkout web voltar a ser usado. Quem resolve
// isso e devolve um formato so e o GET /api/me/subscription.

import React, { useEffect, useState } from 'react';
import { Icon } from './icons';
import { api } from '../services/apiV2';
import { PLAN_NAMES, acessoSemAssinatura } from './plans';
import { isNativeApp } from './platform';

const STATUS_LABEL: Record<string, { text: string; color: string; bg: string }> = {
  active:    { text: 'Ativa',         color: '#4A8A5A', bg: 'rgba(74,138,90,0.15)' },
  pending:   { text: 'Aguardando',    color: '#C8851A', bg: 'rgba(200,133,26,0.15)' },
  overdue:   { text: 'Atrasada',      color: '#C03A2A', bg: 'rgba(192,58,42,0.15)' },
  cancelled: { text: 'Cancelada',     color: 'var(--ink-3)', bg: 'rgba(0,0,0,0.06)' },
  free:      { text: 'Sem assinatura', color: 'var(--ink-3)', bg: 'rgba(0,0,0,0.06)' },
};

function fmtBRL(v: number): string {
  return 'R$ ' + (v || 0).toFixed(2).replace('.', ',');
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

// Onde a assinatura vive. Antes era o tipo de cobrança do gateway web (cartão,
// Pix, boleto); com a compra na loja quem cobra é a Apple ou o Google, e o
// que importa ao leitor é saber onde cancelar.
function fmtOrigem(o: string, label?: string): string {
  if (label) return label;
  if (o === 'loja') return 'Loja do app';
  if (o === 'mercadopago') return 'Mercado Pago';
  return '—';
}

export const MySubscriptionScreen: React.FC<{
  session: any;
  onBack: () => void;
  onUpgrade: () => void; // abre paywall (tipicamente em nova aba via /planos)
  /** O perfil, para saber do teste grátis / indicação / fundador (QA-06). */
  profile?: any;
}> = ({ session, onBack, onUpgrade, profile }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.getMySubscription(session);
      setData(r);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar assinatura');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session) load();
    // eslint-disable-next-line
  }, [session]);

  const handleCancel = async () => {
    if (!window.confirm('Cancelar assinatura?\n\nVocê continua com acesso até o fim do período já pago. Depois vai pra plano grátis automaticamente.')) return;
    setCancelling(true);
    try {
      await api.cancelCheckout(session);
      window.alert('Assinatura cancelada. Você mantém acesso até o fim do período já pago.');
      await load();
    } catch (e: any) {
      window.alert(e?.message || 'Erro ao cancelar assinatura');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="scroll" style={{ paddingBottom: 100 }}>
      <div style={{ padding: '54px 20px 0' }}>
        <button onClick={onBack} style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'var(--paper-2)', border: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="chevron-left" size={16}/>
        </button>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: 'var(--ember)', fontWeight: 600, marginTop: 24,
        }}>Plano e cobrança</div>
        <div className="serif" style={{
          fontSize: 32, lineHeight: 1.05, marginTop: 6, letterSpacing: '-0.01em',
        }}>
          Minha <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>assinatura</span>
        </div>
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          Carregando…
        </div>
      )}

      {error && (
        <div style={{
          margin: '20px 20px 0', padding: 14, borderRadius: 12,
          background: 'rgba(192,58,42,0.08)', border: '1px solid rgba(192,58,42,0.3)',
          color: '#C03A2A', fontSize: 13,
        }}>{error}</div>
      )}

      {data && (
        <div style={{ padding: '24px 20px 0' }}>
          {/* Card principal: plano atual + status. Três estados:
             1. plan === 'free' → mensagem de upgrade
             2. plan != free + assinatura viva (loja ou Mercado Pago)
             3. plan != free SEM assinatura → concedido pelo admin */}
          {(() => {
            const isFreePlan = data.plan === 'free';
            const isAdminGranted = !isFreePlan && !data.subscription;
            // Pente fino (QA-06): no teste grátis o plano é 'free', mas o app
            // está inteiro. "Free · Sem assinatura · Assine pra abrir o app"
            // dizia o contrário do cartão do topo.
            const acesso = isFreePlan ? acessoSemAssinatura(profile) : null;
            const ateTxt = acesso?.ate ? fmtDate(acesso.ate) : '';
            const statusKey = isFreePlan
              ? 'free'
              : (data.plan_status || 'active');
            const status = acesso
              ? { text: acesso.tipo === 'gratis' ? 'Grátis' : acesso.ate ? 'Liberado' : 'Vitalício', color: '#4A8A5A', bg: 'rgba(74,138,90,0.15)' }
              : (STATUS_LABEL[statusKey] || STATUS_LABEL.free);
            const planName = acesso
              ? (acesso.tipo === 'gratis' ? 'App grátis' : acesso.tipo === 'indicacao' ? 'Acesso por indicação' : 'Fundador')
              : isFreePlan ? 'Sem assinatura' : (PLAN_NAMES[data.plan] || data.plan);
            return (
              <div style={{
                padding: 20, borderRadius: 18,
                background: 'linear-gradient(135deg, var(--paper-2), var(--paper))',
                border: '1px solid var(--line)',
                boxShadow: '0 12px 28px -16px rgba(0,0,0,0.18)',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <div className="mono" style={{
                      fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
                      color: 'var(--ink-4)', fontWeight: 600,
                    }}>Plano atual</div>
                    <div className="serif" style={{ fontSize: 28, lineHeight: 1.05, marginTop: 4, color: 'var(--ink)' }}>
                      {planName}
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 10px', borderRadius: 999,
                    fontSize: 11, fontWeight: 600, fontFamily: 'Geist Mono, monospace',
                    letterSpacing: '0.04em',
                    background: status.bg, color: status.color,
                    whiteSpace: 'nowrap',
                  }}>{status.text}</span>
                </div>

                {/* Caso 2: assinatura viva — loja ou Mercado Pago */}
                {data.subscription && (
                  <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {data.subscription.ciclo && (
                      <Row label="Ciclo" value={data.subscription.ciclo === 'anual' ? 'Anual' : 'Mensal'}/>
                    )}
                    {/* Valor só aparece quando sabemos qual é. Preferimos omitir
                        a mostrar um número que não é o que a loja cobra. */}
                    {typeof data.subscription.valor === 'number' && (
                      <Row label="Valor" value={fmtBRL(data.subscription.valor)}/>
                    )}
                    <Row label="Cobrança por" value={fmtOrigem(data.subscription.origem, data.subscription.origemLabel)}/>
                    {data.subscription.proximaCobranca && (
                      <Row
                        label={data.subscription.renovaAutomatico ? 'Próxima cobrança' : 'Acesso até'}
                        value={fmtDate(data.subscription.proximaCobranca)}
                      />
                    )}
                    {!data.subscription.renovaAutomatico && (
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5, marginTop: 2 }}>
                        A renovação automática está desligada — o acesso vai até a data acima.
                      </div>
                    )}
                  </div>
                )}

                {/* Caso 3: admin override (plano pago sem cobrança) */}
                {isAdminGranted && (
                  <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: 'rgba(232,122,59,0.08)', border: '1px solid rgba(232,122,59,0.25)' }}>
                    <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ember)', fontWeight: 600, marginBottom: 4 }}>
                      ★ Plano concedido pelo admin
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                      Sem cobrança recorrente. Acesso direto a todos os recursos do plano <strong style={{ color: 'var(--ink)' }}>{planName}</strong>.
                    </div>
                  </div>
                )}

                {/* Caso 1: sem assinatura ativa */}
                {acesso && (
                  <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                    {acesso.tipo === 'fundador'
                      ? 'Você chegou antes do lançamento: o app inteiro é seu, sem assinatura e sem data para acabar.'
                      : acesso.tipo === 'gratis'
                        ? 'O app inteiro é seu, de graça e sem prazo: registro, caderno, Protocolo, Modo Foco, relatórios e clubes.'
                        : `O app inteiro está liberado até ${ateTxt}, sem cartão.`}
                  </div>
                )}
                {isFreePlan && !acesso && (
                  <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                    Você ainda não tem uma assinatura ativa. Assine pra abrir o app
                    inteiro — leitura e clubes sem limite, o Protocolo do Hábito e as
                    ferramentas de líder.
                  </div>
                )}
              </div>
            );
          })()}

          {/* O bloco "Próximo pagamento" saiu junto com o gateway antigo: ele existia
              para boleto/Pix em aberto, com link de fatura. Nas lojas não há
              fatura pendente para pagar por fora — e apontar pagamento fora da
              loja dentro do app nativo é anti-steering (Apple 3.1.3). A data da
              próxima cobrança já aparece no card acima. */}

          {/* CTAs */}
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* O app é grátis: quem não paga não tem o que assinar aqui. */}
            {data.plan !== 'free' && <button onClick={onUpgrade} style={{
              padding: '14px 18px', borderRadius: 14,
              background: 'var(--ember)', color: 'var(--paper)',
              border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 12px 24px -10px rgba(199,83,26,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              Ver minha assinatura
              <Icon name="arrow-right" size={14} color="var(--paper)" stroke={2.2}/>
            </button>}

            {/* Assinatura de loja NÃO se cancela pelo app: Apple e Google não
                permitem, e o botão antigo chamava o cancelamento do Mercado
                Pago, que não tem nada a ver com a compra. Mandamos para a
                página de assinaturas da própria loja. */}
            {data.plan !== 'free' && data.subscription?.origem === 'loja' && data.subscription?.urlGerenciar && (
              <a
                href={data.subscription.urlGerenciar}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '14px 18px', borderRadius: 14,
                  background: 'transparent', color: 'var(--ink-2)',
                  border: '1px solid var(--line-strong)', fontSize: 13.5, fontWeight: 500,
                  fontFamily: 'inherit', textAlign: 'center', textDecoration: 'none',
                }}
              >
                Gerenciar na {data.subscription.origemLabel || 'loja'}
              </a>
            )}

            {data.plan !== 'free' && data.subscription?.origem === 'mercadopago' && data.subscription?.daAcesso && (
              <button onClick={handleCancel} disabled={cancelling} style={{
                padding: '14px 18px', borderRadius: 14,
                background: 'transparent', color: '#C03A2A',
                border: '1px solid rgba(192,58,42,0.4)', fontSize: 13.5, fontWeight: 500,
                cursor: cancelling ? 'default' : 'pointer', fontFamily: 'inherit',
                opacity: cancelling ? 0.6 : 1,
              }}>
                {cancelling ? 'Cancelando…' : 'Cancelar assinatura'}
              </button>
            )}
          </div>

          {/* Histórico de cobranças */}
          {data.recentEvents && data.recentEvents.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div className="mono" style={{
                fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
                color: 'var(--ember)', fontWeight: 600, marginBottom: 10,
              }}>Histórico recente</div>
              <div style={{
                background: 'var(--paper-2)', borderRadius: 12,
                border: '1px solid var(--line)', overflow: 'hidden',
              }}>
                {data.recentEvents.map((ev: any, i: number) => (
                  <div key={i} style={{
                    padding: '12px 14px',
                    borderTop: i > 0 ? '1px solid var(--line)' : 'none',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                  }}>
                    <div>
                      <div style={{ fontSize: 12.5, color: 'var(--ink)' }}>
                        {labelEvent(ev.event_type)}
                      </div>
                      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.04em', marginTop: 2 }}>
                        {fmtDate(ev.created_at)}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontFamily: 'Geist Mono, monospace', color: 'var(--ink-2)' }}>
                      {ev.amount != null ? fmtBRL(ev.amount) : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {label}
    </span>
    <span style={{ fontSize: 13.5, color: accent ? 'var(--ember)' : 'var(--ink)', fontWeight: accent ? 600 : 400 }}>
      {value}
    </span>
  </div>
);

function labelEvent(type: string): string {
  const map: Record<string, string> = {
    CHECKOUT_STARTED: 'Checkout iniciado',
    PAYMENT_CREATED: 'Cobrança criada',
    PAYMENT_CONFIRMED: 'Pagamento confirmado',
    PAYMENT_RECEIVED: 'Pagamento recebido',
    PAYMENT_OVERDUE: 'Pagamento atrasado',
    PAYMENT_REFUNDED: 'Pagamento estornado',
    PAYMENT_DELETED: 'Cobrança apagada',
    PAYMENT_CHARGEBACK_REQUESTED: 'Chargeback solicitado',
    SUBSCRIPTION_DELETED: 'Assinatura cancelada',
    SUBSCRIPTION_CANCELLED: 'Cancelamento solicitado',
    ADMIN_OVERRIDE: 'Concedido por admin',
    STORE_SUBSCRIPTION_ACTIVE: 'Assinatura ativa na loja',
  };
  return map[type] || type;
}
